/**
 * NotificationsPage
 * Full page view of all notifications with filtering and actions
 */

import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import {
  Bell,
  BellOff,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  Check,
  CheckCheck,
  Filter,
  Loader2,
  ArrowLeft,
  RefreshCw,
  Mail,
  MailOpen,
  MessageSquare,
  Smartphone,
  Globe,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useNotificationContext } from '@/contexts/NotificationContext'
import { useLocalization } from '@/contexts/LocalizationContext'
import type { Notification, NotificationType, NotificationChannel } from '@/types/notifications'

// Filter options
type ReadFilter = 'all' | 'unread' | 'read'
type ChannelFilter = 'all' | NotificationChannel

/**
 * Get icon component based on notification type
 */
function getNotificationIcon(type: NotificationType, className?: string) {
  const baseClass = cn('h-5 w-5', className)
  
  switch (type) {
    case 'success':
      return <CheckCircle2 className={cn(baseClass, 'text-emerald-500')} />
    case 'warning':
      return <AlertTriangle className={cn(baseClass, 'text-amber-500')} />
    case 'error':
      return <XCircle className={cn(baseClass, 'text-red-500')} />
    default:
      return <Info className={cn(baseClass, 'text-blue-500')} />
  }
}

/**
 * Get channel icon
 */
function getChannelIcon(channel: NotificationChannel, className?: string) {
  const baseClass = cn('h-4 w-4', className)
  
  switch (channel) {
    case 'push':
      return <Smartphone className={baseClass} />
    case 'sms':
      return <MessageSquare className={baseClass} />
    case 'email':
      return <Mail className={baseClass} />
    case 'in_app':
      return <Globe className={baseClass} />
    default:
      return <Bell className={baseClass} />
  }
}

/**
 * Get type badge styles
 */
function getTypeBadgeStyles(type: NotificationType): string {
  switch (type) {
    case 'success':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300'
    case 'warning':
      return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300'
    case 'error':
      return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300'
    default:
      return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300'
  }
}

/**
 * Determine navigation path based on notification content
 */
function getNavigationPath(notification: Notification): string {
  const title = notification.title.toLowerCase()
  const body = notification.body.toLowerCase()
  const content = `${title} ${body}`

  if (content.includes('order')) return '/orders'
  if (content.includes('provider')) return '/providers'
  if (content.includes('payout') || content.includes('financial') || content.includes('invoice'))
    return '/financials'
  if (content.includes('user')) return '/users'
  if (content.includes('account') || content.includes('setting'))
    return '/account-settings'
  
  return '/dashboard'
}

/**
 * Single notification card
 */
function NotificationCard({
  notification,
  onMarkAsRead,
  onNavigate,
}: {
  notification: Notification
  onMarkAsRead: (id: number) => Promise<void>
  onNavigate: (path: string) => void
}) {
  const { t } = useLocalization()
  const [isMarking, setIsMarking] = useState(false)

  const handleClick = async () => {
    if (!notification.isRead) {
      setIsMarking(true)
      await onMarkAsRead(notification.id)
      setIsMarking(false)
    }
    onNavigate(getNavigationPath(notification))
  }

  const handleMarkAsRead = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (notification.isRead) return
    setIsMarking(true)
    await onMarkAsRead(notification.id)
    setIsMarking(false)
  }

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all duration-200 hover:shadow-md group',
        !notification.isRead
          ? 'border-s-4 border-s-primary bg-primary/[0.02] hover:bg-primary/[0.04]'
          : 'border-s-4 border-s-transparent hover:bg-muted/50'
      )}
      onClick={handleClick}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex gap-4">
          {/* Icon */}
          <div
            className={cn(
              'flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center',
              notification.type === 'success' && 'bg-emerald-100',
              notification.type === 'warning' && 'bg-amber-100',
              notification.type === 'error' && 'bg-red-100',
              notification.type === 'info' && 'bg-blue-100'
            )}
          >
            {getNotificationIcon(notification.type)}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-2">
            {/* Header row */}
            <div className="flex items-start justify-between gap-3">
              <h3
                className={cn(
                  'text-base leading-tight',
                  !notification.isRead
                    ? 'font-semibold text-foreground'
                    : 'font-medium text-foreground/80'
                )}
              >
                {notification.title}
              </h3>
              
              <div className="flex items-center gap-2 flex-shrink-0">
                {!notification.isRead && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={handleMarkAsRead}
                    disabled={isMarking}
                  >
                    {isMarking ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <>
                        <Check className="h-3.5 w-3.5 me-1" />
                        <span className="text-xs">{t('notifications.markRead')}</span>
                      </>
                    )}
                  </Button>
                )}
                {!notification.isRead ? (
                  <Mail className="h-4 w-4 text-primary" />
                ) : (
                  <MailOpen className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </div>

            {/* Body */}
            <p className="text-sm text-muted-foreground leading-relaxed">
              {notification.body}
            </p>

            {/* Footer */}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Badge
                variant="outline"
                className={cn('text-xs capitalize', getTypeBadgeStyles(notification.type))}
              >
                {notification.status}
              </Badge>
              
              <Badge variant="outline" className="text-xs gap-1">
                {getChannelIcon(notification.channel)}
                <span className="capitalize">{notification.channel.replace('_', ' ')}</span>
              </Badge>

              <span className="text-xs text-muted-foreground ms-auto">
                {format(notification.createdAt, 'MMM d, yyyy')} at{' '}
                {format(notification.createdAt, 'h:mm a')}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Empty state component
 */
function EmptyState({ hasFilters }: { hasFilters: boolean }) {
  const { t } = useLocalization()
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mb-4">
          <BellOff className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1">
          {hasFilters ? t('notifications.noMatchingNotifications') : t('notifications.noNotificationsYet')}
        </h3>
        <p className="text-sm text-muted-foreground text-center max-w-sm">
          {hasFilters
            ? t('notifications.tryAdjustingFilters')
            : t('notifications.allCaughtUp')}
        </p>
      </CardContent>
    </Card>
  )
}

/**
 * Loading skeleton
 */
function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <Card key={i}>
          <CardContent className="p-5">
            <div className="flex gap-4">
              <Skeleton className="h-10 w-10 rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-3">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-1/2" />
                <div className="flex gap-2 pt-1">
                  <Skeleton className="h-5 w-16" />
                  <Skeleton className="h-5 w-16" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

/**
 * Stats card component
 */
function StatsCard({
  label,
  value,
  icon: Icon,
  variant = 'default',
}: {
  label: string
  value: number
  icon: React.ElementType
  variant?: 'default' | 'primary' | 'success'
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-lg',
        variant === 'default' && 'bg-muted/50',
        variant === 'primary' && 'bg-primary/10',
        variant === 'success' && 'bg-emerald-50 dark:bg-emerald-950/30'
      )}
    >
      <Icon
        className={cn(
          'h-5 w-5',
          variant === 'default' && 'text-muted-foreground',
          variant === 'primary' && 'text-primary',
          variant === 'success' && 'text-emerald-600'
        )}
      />
      <div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  )
}

/**
 * Main NotificationsPage Component
 */
export function NotificationsPage() {
  const { t } = useLocalization()
  const navigate = useNavigate()
  const {
    notifications,
    unreadCount,
    totalCount,
    loading,
    isFetching,
    markAsRead,
    markAllAsRead,
    refetch,
  } = useNotificationContext()

  // Filter state
  const [readFilter, setReadFilter] = useState<ReadFilter>('all')
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all')
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false)

  // Filter notifications
  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      // Read filter
      if (readFilter === 'unread' && notification.isRead) return false
      if (readFilter === 'read' && !notification.isRead) return false
      
      // Channel filter
      if (channelFilter !== 'all' && notification.channel !== channelFilter) return false
      
      return true
    })
  }, [notifications, readFilter, channelFilter])

  const hasFilters = readFilter !== 'all' || channelFilter !== 'all'
  const readCount = notifications.filter((n) => n.isRead).length

  const handleMarkAsRead = useCallback(
    async (id: number) => {
      try {
        await markAsRead(id)
      } catch (error) {
        console.error('Failed to mark as read:', error)
      }
    },
    [markAsRead]
  )

  const handleMarkAllAsRead = useCallback(async () => {
    setIsMarkingAllRead(true)
    try {
      await markAllAsRead()
    } catch (error) {
      console.error('Failed to mark all as read:', error)
    } finally {
      setIsMarkingAllRead(false)
    }
  }, [markAllAsRead])

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path)
    },
    [navigate]
  )

  const handleRefresh = useCallback(() => {
    refetch()
  }, [refetch])

  return (
    <div className="container max-w-4xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 -ms-2"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Notifications</h1>
            <p className="text-sm text-muted-foreground">
              {unreadCount > 0
                ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                : 'All caught up!'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            <RefreshCw
              className={cn('h-4 w-4 me-2', isFetching && 'animate-spin')}
            />
            Refresh
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={isMarkingAllRead}
            >
              {isMarkingAllRead ? (
                <Loader2 className="h-4 w-4 me-2 animate-spin" />
              ) : (
                <CheckCheck className="h-4 w-4 me-2" />
              )}
              Mark all as read
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <StatsCard
          label="Total"
          value={totalCount}
          icon={Bell}
          variant="default"
        />
        <StatsCard
          label="Unread"
          value={unreadCount}
          icon={Mail}
          variant="primary"
        />
        <StatsCard
          label="Read"
          value={readCount}
          icon={CheckCircle2}
          variant="success"
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <Select
                value={readFilter}
                onValueChange={(value) => setReadFilter(value as ReadFilter)}
              >
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue placeholder="Read status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All notifications</SelectItem>
                  <SelectItem value="unread">Unread only</SelectItem>
                  <SelectItem value="read">Read only</SelectItem>
                </SelectContent>
              </Select>

              <Select
                value={channelFilter}
                onValueChange={(value) => setChannelFilter(value as ChannelFilter)}
              >
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue placeholder="Channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All channels</SelectItem>
                  <SelectItem value="push">Push</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="in_app">In-app</SelectItem>
                </SelectContent>
              </Select>

              {hasFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-9"
                  onClick={() => {
                    setReadFilter('all')
                    setChannelFilter('all')
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Separator />

      {/* Notification List */}
      {loading ? (
        <LoadingSkeleton />
      ) : filteredNotifications.length === 0 ? (
        <EmptyState hasFilters={hasFilters} />
      ) : (
        <div className="space-y-3">
          {filteredNotifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkAsRead={handleMarkAsRead}
              onNavigate={handleNavigate}
            />
          ))}
        </div>
      )}

      {/* Results summary */}
      {!loading && filteredNotifications.length > 0 && (
        <p className="text-center text-sm text-muted-foreground">
          Showing {filteredNotifications.length} of {totalCount} notification
          {totalCount !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  )
}
